import * as React from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useBoolean, useHover, useDebounceEffect } from 'ahooks'
import styled from 'styled-components'
import { fadeConfig } from '../../config/motion'
import { cloneElement } from '../_util/reactNode'

const DIRECTION_MAP = {
  top: 'top' as const,
  topLeft: 'top-start' as const,
  topRight: 'top-end' as const,
  bottom: 'bottom' as const,
  bottomLeft: 'bottom-start' as const,
  bottomRight: 'bottom-end' as const,
}

const ARROW_OFFSET = 18.5 as const

const ARROW_POSITION = { start: '6%' as const, end: '94%' as const }

const Trigger = styled(motion.div)`
  position: fixed;
  z-index: 999999;
  .tooltip {
    position: relative;
    .content {
      padding: 14px 10px;
      max-width: 300px;
      border-radius: 12px;
      background: #595959;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
    }
    .arrow {
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
    }
  }
  .tooltip-top {
    padding-bottom: 10px;
    .arrow {
      bottom: 3px;
      border-width: 7px 7px 0 7px;
      border-color: #595959 transparent transparent transparent;
    }
  }
  .tooltip-bottom {
    padding-top: 10px;
    .arrow {
      top: 3px;
      border-width: 0 7px 7px 7px;
      border-color: transparent transparent #595959 transparent;
    }
  }
`

type TooltipPlacement = 'top' | 'topLeft' | 'topRight' | 'bottom' | 'bottomLeft' | 'bottomRight'

export interface TooltipProps {
  placement?: TooltipPlacement
  title?: React.ReactNode
  children?: React.ReactNode
}

interface TriggerProps extends TooltipProps {
  followRef: React.RefObject<HTMLDivElement>
  followID?: string
}

interface PositionProps {
  top: number
  left: number
}

const Portal: React.FC<TriggerProps> = (props: TriggerProps) => {
  const { followRef, followID, placement = 'top', title } = props

  const innerRef = React.useRef<HTMLDivElement>(null)
  const [direction, setDirection] = React.useState<PositionProps>()
  const [extraX, extraY] = DIRECTION_MAP[placement].split('-')

  const filterPosition = React.useCallback(
    (follow: DOMRect, inside: DOMRect) => {
      const { top: followTop, left: followLeft, width: followWidth, height: followHeight } = follow
      const { width: innerWidth, height: innerHeight } = inside
      const siteMap = {
        start: followLeft - ARROW_OFFSET,
        center: followWidth / 2 + followLeft - innerWidth / 2,
        end: followLeft + followWidth - innerWidth + ARROW_OFFSET,
      }
      const size: PositionProps = {
        top: extraX === 'top' ? followTop - innerHeight : followTop + followHeight,
        left: siteMap[extraY || 'center'],
      }
      return size
    },
    [extraX, extraY],
  )

  React.useEffect(() => {
    if (!followRef.current || !innerRef.current) return
    const follow = followRef.current.getBoundingClientRect()
    const inside = innerRef.current.getBoundingClientRect()
    const result = filterPosition(follow, inside)
    setDirection(result)
  }, [followRef, innerRef, filterPosition])

  const memoElement = React.useMemo(() => {
    const arrowMove = ARROW_POSITION[extraY] || '50%'
    return (
      <Trigger ref={innerRef} id={followID} style={direction} onClick={(e) => e.stopPropagation()} {...fadeConfig}>
        <div className={`tooltip tooltip-${extraX}`}>
          <div className="content">{cloneElement(title)}</div>
          <div className="arrow" style={{ left: arrowMove, transform: `translateX(-${arrowMove})` }} />
        </div>
      </Trigger>
    )
  }, [extraY, followID, direction, title, extraX])

  return createPortal(memoElement, window.document.body as HTMLElement)
}

const Tooltip: React.FC<TooltipProps> = (props: TooltipProps) => {
  const { children } = props

  const followRef = React.useRef<HTMLDivElement>(null)
  const uuid = React.useId()

  const [state, { setTrue, setFalse }] = useBoolean(false)

  // handle event
  const hoverFollow = useHover(followRef, { onEnter: setTrue })
  const hoverPortal = useHover(() => window.document.getElementById(uuid), {
    onLeave: setFalse,
  })

  const handleClose = React.useCallback(() => {
    if (hoverFollow || hoverPortal) return
    setFalse()
  }, [hoverFollow, hoverPortal, setFalse])

  useDebounceEffect(() => handleClose(), [hoverFollow, hoverPortal], { wait: 150 })

  return (
    <>
      {cloneElement(children, { ref: followRef })}
      <AnimatePresence>{state && <Portal followRef={followRef} followID={uuid} {...props} />}</AnimatePresence>
    </>
  )
}

export default Tooltip
